import { AfterViewInit, Directive, ElementRef, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudItemComponent, UserStorage, requiredValidator } from '@scisys-uim/uim-common';
import { ASSET_TYPE, ASSET_TYPE_METADATA, ASSET_TYPE_TYPE, Asset, AssetType, AssetsUser, BUSINESS_UNIT_METADATA, BUSINESS_UNIT_TYPE, BusinessUnit, COST_CENTRE_METADATA, COST_CENTRE_TYPE, CostCentre, ELECTRICAL_ASPECT_METADATA, ELECTRICAL_ASPECT_TYPE, ElectricalAspect, MAINTENANCE_ASPECT_METADATA, MAINTENANCE_ASPECT_TYPE, MECHANICAL_ASPECT_METADATA, MECHANICAL_ASPECT_TYPE, MaintenanceAspect, MechanicalAspect, RefCode, setDefaultLocationFromUser } from 'assets-model';
import { AssetService, AssetTypeService, BusinessUnitService, CostCentreService, ElectricalAspectService, MaintenanceAspectService, MechanicalAspectService, RefCodeStorage } from 'assets-service';


@Directive()
export abstract class AssetItemComponentBase extends CrudItemComponent<Asset, number> implements OnInit, AfterViewInit {

  protected systemConfig: any;
  
  public form: FormGroup;
  
  assetTypeSelectionList: AssetType[];

  businessUnitSelectionList: BusinessUnit[];

  costCentreSelectionList: CostCentre[];

  electricalAspectSelectionList: ElectricalAspect[];

  maintenanceAspectSelectionList: MaintenanceAspect[];

  mechanicalAspectSelectionList: MechanicalAspect[];

  composedAssetClassSelectionList: RefCode[];

  statusSelectionList: RefCode[];


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected assetService: AssetService,
    protected assetTypeService: AssetTypeService,
    protected businessUnitService: BusinessUnitService,
    protected costCentreService: CostCentreService,
    protected electricalAspectService: ElectricalAspectService,
    protected maintenanceAspectService: MaintenanceAspectService,
    protected mechanicalAspectService: MechanicalAspectService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(ASSET_TYPE, fb, elementRef, router, route, userStorage, dialog, assetService, injector, snackbar);
  }

  createNewItem() {
    return new Asset();
  }
  
  createForm(): FormGroup {
    return this.fb.group({
      assetId: [null, []],
      assetType: [null, []],
      businessUnit: [null, []],
      costCentre: [null, []],
      electricalAspect: [null, []],
      maintenanceAspect: [null, []],
      mechanicalAspect: [null, []],
      comments: [null, []],
      composedAssetClass: [null, [requiredValidator,]],
      createdBy: [null, []],
      createdDate: [null, []],
      description: [null, []],
      identityReference: [null, []],
      importDataMap: [null, []],
      latestRoutineMaintenanceDate: [null, []],
      location: [null, []],
      lockVersion: [null, []],
      manufacturer: [null, []],
      model: [null, []],
      nextRoutineMaintenanceRequiredDate: [null, []],
      nextRoutineMaintenanceWarningDate: [null, []],
      purchaseDate: [null, []],
      purchasePrice: [null, []],
      status: [null, [requiredValidator,]],
      updatedBy: [null, []],
      updatedDate: [null, []]
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }
  
  protected initSystemConfig(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initSystemConfig(config);
  }
  
  setDefaults() {
    if (!this.readOnly && this.createMode) {
      setDefaultLocationFromUser(this.userDetails, this.item);

      // re-trigger updating drop-downs.
      this.itemSet();
    }
    this.setDefaultDisabledFields();
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    if (!this.createMode) {
      this.assetId?.disable();
    }
    this.latestRoutineMaintenanceDate?.disable();
    this.nextRoutineMaintenanceRequiredDate?.disable();
    this.nextRoutineMaintenanceWarningDate?.disable();
  }
  
  assetIdChanging(value: number, unsetDependents: boolean = true) {}
  
  assetIdChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.assetIdChangedByUser(value, unsetDependents);
    }
  }  
  
  assetIdChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  assetTypeDisplay(item: AssetType) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return ASSET_TYPE_TYPE.description(item);
  }
  
  assetTypeLoadSelectionList() {
    this.assetTypeService.findAll().subscribe(
      data => {
        this.assetTypeSelectionList = ASSET_TYPE_TYPE.entityDecorator.ensureContained(this.item && this.item.assetType, data.items);
      }
    );
  }

  assetTypeChanging(value: AssetType, unsetDependents: boolean = true) {}
  
  assetTypeChanged(value: AssetType, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.assetTypeChangedByUser(value, unsetDependents);
    }
  }  
  
  assetTypeChangedByUser(value: AssetType, unsetDependents: boolean = true) {
  }
  
  assetTypeCompare(a: any, b: any) {
    return ASSET_TYPE_METADATA.equals(a, b);
  }
  
  businessUnitDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return BUSINESS_UNIT_TYPE.description(item);
  }
  
  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll().subscribe(
      data => {
        this.businessUnitSelectionList = BUSINESS_UNIT_TYPE.entityDecorator.ensureContained(this.item && this.item.businessUnit, data.items);
      }
    );
  }

  businessUnitChanging(value: BusinessUnit, unsetDependents: boolean = true) {}
  
  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.businessUnitChangedByUser(value, unsetDependents);
    }
  }  
  
  businessUnitChangedByUser(value: BusinessUnit, unsetDependents: boolean = true) {
  }
  
  businessUnitCompare(a: any, b: any) {
    return BUSINESS_UNIT_METADATA.equals(a, b);
  }
  
  costCentreDisplay(item: CostCentre) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return COST_CENTRE_TYPE.description(item);
  }
  
  costCentreLoadSelectionList() {
    this.costCentreService.findAll().subscribe(
      data => {
        this.costCentreSelectionList = COST_CENTRE_TYPE.entityDecorator.ensureContained(this.item && this.item.costCentre, data.items);
      }
    );
  }

  costCentreChanging(value: CostCentre, unsetDependents: boolean = true) {}
  
  costCentreChanged(value: CostCentre, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.costCentreChangedByUser(value, unsetDependents);
    }
  }  
  
  costCentreChangedByUser(value: CostCentre, unsetDependents: boolean = true) {
  }
  
  costCentreCompare(a: any, b: any) {
    return COST_CENTRE_METADATA.equals(a, b);
  }
  
  electricalAspectDisplay(item: ElectricalAspect) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return ELECTRICAL_ASPECT_TYPE.description(item);
  }
  
  electricalAspectLoadSelectionList() {
    this.electricalAspectService.findAll().subscribe(
      data => {
        this.electricalAspectSelectionList = ELECTRICAL_ASPECT_TYPE.entityDecorator.ensureContained(this.item && this.item.electricalAspect, data.items);
      }
    );
  }

  electricalAspectChanging(value: ElectricalAspect, unsetDependents: boolean = true) {}
  
  electricalAspectChanged(value: ElectricalAspect, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.electricalAspectChangedByUser(value, unsetDependents);
    }
  }  
  
  electricalAspectChangedByUser(value: ElectricalAspect, unsetDependents: boolean = true) {
  }
  
  electricalAspectCompare(a: any, b: any) {
    return ELECTRICAL_ASPECT_METADATA.equals(a, b);
  }
  
  maintenanceAspectDisplay(item: MaintenanceAspect) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return MAINTENANCE_ASPECT_TYPE.description(item);
  }
  
  maintenanceAspectLoadSelectionList() {
    this.maintenanceAspectService.findAll().subscribe(
      data => {
        this.maintenanceAspectSelectionList = MAINTENANCE_ASPECT_TYPE.entityDecorator.ensureContained(this.item && this.item.maintenanceAspect, data.items);
      }
    );
  }

  maintenanceAspectChanging(value: MaintenanceAspect, unsetDependents: boolean = true) {}
  
  maintenanceAspectChanged(value: MaintenanceAspect, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.maintenanceAspectChangedByUser(value, unsetDependents);
    }
  }  
  
  maintenanceAspectChangedByUser(value: MaintenanceAspect, unsetDependents: boolean = true) {
  }
  
  maintenanceAspectCompare(a: any, b: any) {
    return MAINTENANCE_ASPECT_METADATA.equals(a, b);
  }
  
  mechanicalAspectDisplay(item: MechanicalAspect) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return MECHANICAL_ASPECT_TYPE.description(item);
  }
  
  mechanicalAspectLoadSelectionList() {
    this.mechanicalAspectService.findAll().subscribe(
      data => {
        this.mechanicalAspectSelectionList = MECHANICAL_ASPECT_TYPE.entityDecorator.ensureContained(this.item && this.item.mechanicalAspect, data.items);
      }
    );
  }

  mechanicalAspectChanging(value: MechanicalAspect, unsetDependents: boolean = true) {}
  
  mechanicalAspectChanged(value: MechanicalAspect, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.mechanicalAspectChangedByUser(value, unsetDependents);
    }
  }  
  
  mechanicalAspectChangedByUser(value: MechanicalAspect, unsetDependents: boolean = true) {
  }
  
  mechanicalAspectCompare(a: any, b: any) {
    return MECHANICAL_ASPECT_METADATA.equals(a, b);
  }
  
  commentsChanging(value: string, unsetDependents: boolean = true) {}
  
  commentsChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.commentsChangedByUser(value, unsetDependents);
    }
  }  
  
  commentsChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  composedAssetClassLoadSelectionList() {
    this.refCodeStorage.getDomainElements('SYSTEM_COMPOSED_ASSET_CLASS').subscribe(
      data => {
        this.composedAssetClassSelectionList = data;
      }
    );
  }

  composedAssetClassChanging(value: string, unsetDependents: boolean = true) {}
  
  composedAssetClassChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.composedAssetClassChangedByUser(value, unsetDependents);
    }
  }  
  
  composedAssetClassChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdByChanging(value: string, unsetDependents: boolean = true) {}
  
  createdByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdByChangedByUser(value, unsetDependents);
    }
  }  
  
  createdByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdDateChangedByUser(value, unsetDependents);
    }
  }  
  
  createdDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  descriptionChanging(value: string, unsetDependents: boolean = true) {}
  
  descriptionChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.descriptionChangedByUser(value, unsetDependents);
    }
  }  
  
  descriptionChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  identityReferenceChanging(value: string, unsetDependents: boolean = true) {}
  
  identityReferenceChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.identityReferenceChangedByUser(value, unsetDependents);
    }
  }  
  
  identityReferenceChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  importDataMapChanging(value: { [id: string]: any; }, unsetDependents: boolean = true) {}
  
  importDataMapChanged(value: { [id: string]: any; }, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.importDataMapChangedByUser(value, unsetDependents);
    }
  }  
  
  importDataMapChangedByUser(value: { [id: string]: any; }, unsetDependents: boolean = true) {
  }
  
  
  locationChanging(value: string, unsetDependents: boolean = true) {}
  
  locationChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.locationChangedByUser(value, unsetDependents);
    }
  }  
  
  locationChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  lockVersionChanging(value: number, unsetDependents: boolean = true) {}
  
  lockVersionChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.lockVersionChangedByUser(value, unsetDependents);
    }
  }  
  
  lockVersionChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  manufacturerChanging(value: string, unsetDependents: boolean = true) {}
  
  manufacturerChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.manufacturerChangedByUser(value, unsetDependents);
    }
  }  
  
  manufacturerChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  modelChanging(value: string, unsetDependents: boolean = true) {}
  
  modelChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.modelChangedByUser(value, unsetDependents);
    }
  }  
  
  modelChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  
  
  purchaseDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  purchaseDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.purchaseDateChangedByUser(value, unsetDependents);
    }
  }  
  
  purchaseDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  purchasePriceChanging(value: number, unsetDependents: boolean = true) {}
  
  purchasePriceChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.purchasePriceChangedByUser(value, unsetDependents);
    }
  }  
  
  purchasePriceChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  statusLoadSelectionList() {
    this.refCodeStorage.getDomainElements('ASSET_STATUS').subscribe(
      data => {
        this.statusSelectionList = data;
      }
    );
  }

  statusChanging(value: string, unsetDependents: boolean = true) {}
  
  statusChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.statusChangedByUser(value, unsetDependents);
    }
  }  
  
  statusChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  updatedByChanging(value: string, unsetDependents: boolean = true) {}
  
  updatedByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedByChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  updatedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedDateChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  get assetId() {
    return this.control('assetId');
  }
  
  get assetType() {
    return this.control('assetType');
  }
  
  get businessUnit() {
    return this.control('businessUnit');
  }
  
  get costCentre() {
    return this.control('costCentre');
  }
  
  get electricalAspect() {
    return this.control('electricalAspect');
  }
  
  get maintenanceAspect() {
    return this.control('maintenanceAspect');
  }
  
  get mechanicalAspect() {
    return this.control('mechanicalAspect');
  }
  
  get comments() {
    return this.control('comments');
  }
  
  get composedAssetClass() {
    return this.control('composedAssetClass');
  }
  
  get createdBy() {
    return this.control('createdBy');
  }
  
  get createdDate() {
    return this.control('createdDate');
  }
  
  get description() {
    return this.control('description');
  }
  
  get identityReference() {
    return this.control('identityReference');
  }
  
  get importDataMap() {
    return this.control('importDataMap');
  }
  
  get latestRoutineMaintenanceDate() {
    return this.control('latestRoutineMaintenanceDate');
  }
  
  get location() {
    return this.control('location');
  }
  
  get lockVersion() {
    return this.control('lockVersion');
  }
  
  get manufacturer() {
    return this.control('manufacturer');
  }
  
  get model() {
    return this.control('model');
  }
  
  get nextRoutineMaintenanceRequiredDate() {
    return this.control('nextRoutineMaintenanceRequiredDate');
  }
  
  get nextRoutineMaintenanceWarningDate() {
    return this.control('nextRoutineMaintenanceWarningDate');
  }
  
  get purchaseDate() {
    return this.control('purchaseDate');
  }
  
  get purchasePrice() {
    return this.control('purchasePrice');
  }
  
  get status() {
    return this.control('status');
  }
  
  get updatedBy() {
    return this.control('updatedBy');
  }
  
  get updatedDate() {
    return this.control('updatedDate');
  }
  
}





